import React, { useState, useCallback } from 'react';
import UndoFitReasonModal from 'src/components/undo-fit-reason-modal';
import {
	Modal,
	Row,
	Col,
	Input,
	Button,
	Tooltip,
	Checkbox,
	Radio,
} from 'antd';
import { noop } from 'lodash';
import { useTranslation } from 'react-i18next';
import DoubleArrowImage from 'src/assets/img/double-arrow.svg';
import { filterObjectsArrayByProperty } from 'src/utils/table-util';
import PeopleItem from 'src/components/people-item';
import NeedsItem from 'src/components/need-item';
import FormItem from 'src/components/form-item';
import ConfirmFitModal from 'src/pages/people/confirm-fit-modal';
import { FitPeopleLabel } from 'src/components/fit-people-label';
import { filteredAndRejected } from 'src/utils/helpers';
import { UndoOutlined } from '@ant-design/icons';
import Exclamation from 'src/assets/img/icon_exclamation_circle.svg' 
import './style.scss';

const { Search } = Input;

/**
 * Checks if two arrays are equal, considering the order of elements.
 *
 * @param {Array} arr1 - The first array to compare.
 * @param {Array} arr2 - The second array to compare.
 * @returns {Boolean} `true` if the arrays are equal, `false` otherwise.
 */
const areEqualArrays = (arr1, arr2) => {
	const sortedArr1 = [...arr1].sort();
	const sortedArr2 = [...arr2].sort();

	return JSON.stringify(sortedArr1) === JSON.stringify(sortedArr2);
};

/**
 * Retrieves the deallocation IDs and fit IDs from the provided need object.
 *
 * @param {Object} need - The need object.
 * @returns {Array} An array of objects containing deallocationId and fitId.
 */
const getNeedDeallocationsIds = (need) => {
	const deallocationsIds = need.deallocations.map((dealloc) => {
		if (dealloc.Fits.active === 'Y' && dealloc.Fits.match === 'N')
			return {
				deallocationId: dealloc.id,
				fitId: dealloc.Fits.id,
				responsibleId: dealloc.Fits.responsibleId,
			};
	});

	if (deallocationsIds.length === 0) return [];

	return deallocationsIds;
};

/**
 * Retrieves deallocations with suggestions that match the given need.
 *
 * @param {object} need - The need object.
 * @param {Array} deallocations - The array of deallocations to search through.
 * @returns {Array} An array of deallocations with matching suggestions.
 */
const getDeallocationsWithSuggestions = (need, deallocations) => {
	const suggestionDeallocations = deallocations.map((dealloc) => {
		const suggestion = dealloc.deallocation_suggestion.find(
			(suggestion) => suggestion.requisitionCode === need.requisitionCode
		);
		if (suggestion) {
			return {
				...dealloc,
				suggestion: suggestion,
			};
		}

		return null;
	});

	return suggestionDeallocations.filter((dealloc) => dealloc !== null);
};

/**
 * FitModal component to show options of deallocations in order to create a fit with given need.
 *
 * @param {Boolean} loading - Indicates whether the component is in a loading state.
 * @param {Object} need - The need object to be matched.
 * @param {Object} props.user - The logged user object.
 * @param {Array<Object>} deallocations - The array of deallocations to display and select for fitting.
 * @param {Function} onSave - The function to be called when the fit is saved.
 * @param {Function} onClose - The function to be called when the modal is closed or canceled.
 * @param {Object} positionMaps - The position maps for mapping positions between the need and deallocations.
 * @param {Function} onCancelFit - The function to be called when a fit is canceled.
 * @returns {JSX.Element} The FitModal component.
 * @example
 * 	<FitModal
		loading={formFetching}
		need={fitNeedObj}
		user={user}
		deallocations={fitDeallocationsArr}
		onSave={createFitFn}
		onClose={closeFn}
		positionMaps={positionMapsObj}
		onCancelFit={cancelFitFn}
	/>
 */
function FitModal({
	loading,
	need,
	user,
	deallocations,
	onSave,
	onClose,
	positionMaps,
	onCancelFit,
}) {
	const [undoFitId, setUndoFitId] = useState(null);
	const [undoFitLoading, setUndoFitLoading] = useState(false);


	// only deallocations that don't have a fit already with other needs
	const filteredDeallocations = (
		deallocations.filter((dealloc) => {
			if (dealloc.needs.length === 0) return true;
			return dealloc.needs.find((deallocNeed) => deallocNeed.id !== need.id)
				? false
				: true;
		})
	);
	const needId = [need.id];
	const optMatchDeallocations = getDeallocationsWithSuggestions(need, filteredDeallocations);
	const fitDeallocations = getNeedDeallocationsIds(need);
	const deallocationFitIds = fitDeallocations.map((dealloc) => dealloc.deallocationId);

	const [filter, setFilter] = useState('');
	const [deallocationIds, setDeallocationIds] = useState([]);
	const [isFiltered, setIsFiltered] = useState(true);
	const [isConfirming, setIsConfirming] = useState(null)
  const [fitData, setFitData] = useState([])

	const { t } = useTranslation();

	/**
	 * Set new array of deallocation Ids upon changing selection.
	 */
	const onChangeDeallocation = useCallback(
		(deallocationId) => {
			setDeallocationIds([...deallocationIds, deallocationId]);
		},
		[deallocationIds, setDeallocationIds]
	);

	/**
	 * Remove unselected items from array of deallocationIds.
	 */
	const onUncheckDeallocation = useCallback(
		(deallocationId) => {
			// remove deallocationId on box uncheck
			const deallocationIdsAfterRemoval = deallocationIds.filter(
				(id) => id !== deallocationId
			);
			if (deallocationIdsAfterRemoval.length > 0) {
				setDeallocationIds(deallocationIdsAfterRemoval);
			} else {
				setDeallocationIds([]);
			}
		},
		[deallocationIds]
	);

	/**
	 * Organize an array of sorted deallocations, in this order:
	 * - deallocations with a Fit and Opt match suggestion.
	 * - deallocations with a Fit only.
	 * - deallocations without a Fit but with Opt match suggestion.
	 * - deallocations with neither a Fit nor an Opt match suggestion.
	 */
	const orderedResult = (() => {
		// separate deallocations with Fit (fitsPeople) and without fit (fits)
		const [fitsPeople, fits] = filteredAndRejected(
			filteredDeallocations,
			(item) => deallocationFitIds.includes(item.id)
		);

		const compare = (a, b) =>
			a.collaborator_deallocation.login.localeCompare(
				b.collaborator_deallocation.login
			);

		const compareOptMatch = (a, b) =>
			b.suggestion.optMatchPercent.localeCompare(a.suggestion.optMatchPercent);

		fitsPeople.forEach((fit) => (fit.isFitNeed = true));

		// fits with opt match
		const optMatchFitDeallocations = getDeallocationsWithSuggestions(
			need,
			fitsPeople
		);
		optMatchFitDeallocations.sort(compareOptMatch);

		// fits without opt match
		const otherFits = fitsPeople.filter(
			(fit) =>
				!optMatchFitDeallocations.find((dealloc) => dealloc.id === fit.id)
		);
		otherFits.sort(compare);

		// avoid repeating optMatch suggestions with others
		const otherNonFits = fits.filter(
			(filterDeallocation) =>
				!optMatchDeallocations.find(
					(deallocation) => deallocation.id === filterDeallocation.id
				)
		);
		otherFits.sort(compare);

		// if opt match suggestion is already a fit, avoid returning it twice
		const optMatchNonFitDeallocations = optMatchDeallocations.filter(
			(filterDeallocation) =>
				!fitsPeople.find(
					(deallocation) => deallocation.id === filterDeallocation.id
				)
		);
		optMatchNonFitDeallocations.sort(compareOptMatch);

		return [
			...optMatchFitDeallocations,
			...otherFits,
			...optMatchNonFitDeallocations,
			...otherNonFits,
		];
	})();

	/**
	 * Apply input filters to options.
	 */
	const resultDeallocations = filterObjectsArrayByProperty(
		orderedResult,
		[
			'collaborator_deallocation.login',
			'collaborator_deallocation.role',
			'collaborator_deallocation.positionMap',
			'collaborator_deallocation.name',
			'collaborator_deallocation.gu',
			'collaborator_deallocation.alliance',
			'collaborator_deallocation.site',
			'collaborator_deallocation.pdm',
		],
		filter
	);

	/**
	 * Retrieves the selected position maps based on the given need and position maps.
	 *
	 * @param {Object} need - The need object.
	 * @param {Array} positionMaps - The array of position maps.
	 * @returns {Array} The selected position maps.
	 */
	const getSelectedPositionMaps = (need, positionMaps) => {
		const needLevel = need.level;
		const currentIndex = positionMaps.findIndex(
			(item) => item[0] === needLevel[0]
		); // compare their numbers

		const selectedPositionMaps = [];
		if (currentIndex !== -1) {
			selectedPositionMaps.push(positionMaps[currentIndex]);
			// deallocations with +1 superior position map
			if (currentIndex < positionMaps.length - 1) {
				selectedPositionMaps.push(positionMaps[currentIndex + 1]);
			}
			// deallocations with -1 inferior position map
			if (currentIndex > 1) {
				selectedPositionMaps.push(positionMaps[currentIndex - 1]);
			}
		}

		return selectedPositionMaps;
	};

	/**
	 * Renders a checkbox component based on the provided parameters.
	 * 
	 * @param {string|null} [labelType] - The type of label (optional) - to apply Fit, Opt Match or Fit + Opt Match labels.
	 * @param {object} dealloc - The deallocation object.
	 * @returns {JSX.Element} The rendered checkbox component.
	 */

	const renderCheckbox = (labelType = null, dealloc) => {
		if (labelType) {
			return (
				<FitPeopleLabel type={labelType}>
					<Checkbox
						value={dealloc.id}
						onChange={(e) =>
							e.target.checked
								? onChangeDeallocation(dealloc.id)
								: onUncheckDeallocation(dealloc.id)
						}
						className='opportunity-fit__checkbox'
						aria-label={t('selectFit', {type: t('peopleTitle'), title: dealloc.collaborator_deallocation.login})}
					>
						<span className='opportunity-fit__checkbox_text'>Fit</span>
						<PeopleItem people={dealloc} className={'remove-border'} />
					</Checkbox>
				</FitPeopleLabel>
			);
		} else {
			return (
				<Checkbox
					value={dealloc.id}
					onChange={(e) =>
						e.target.checked
							? onChangeDeallocation(dealloc.id)
							: onUncheckDeallocation(dealloc.id)
					}
					className='opportunity-fit__checkbox non-fit-checkbox'
					aria-label={t('selectFit', {type: t('peopleTitle'), title: dealloc.collaborator_deallocation.login})}
				>
					<span className='opportunity-fit__checkbox_text'>Fit</span>
					<PeopleItem people={dealloc} className={'remove-border'} />
				</Checkbox>
			);
		}
	};

	/**
	 * Render selection of deallocations to appear.
	 */
	const deallocationsOptions = resultDeallocations
		.filter((dealloc) => {
			if (isFiltered) {
				const selectedPositionMaps = getSelectedPositionMaps(
					need, positionMaps
				);

				if ((dealloc.suggestion?.optMatchPercent && dealloc.suggestion?.matchDateSuggestion) || dealloc.isFitNeed) {
					// no filters applied to opt match suggestions or needs that are fits
					return dealloc;
				}

				const isAvailableForRole =
					dealloc.collaborator_deallocation.role === need.role &&
					selectedPositionMaps.some(
						(positionMap) => positionMap[0] === dealloc.collaborator_deallocation.positionMap[0]);

				return isAvailableForRole;
			}

			return dealloc;
		})
		.map((dealloc) => {
			const isPeopleSuggestion = deallocationFitIds.includes(dealloc.id);
			let activeFitId = null;
			let activeFitObject = null;
			let labelType = '';
			if (isPeopleSuggestion) {
				labelType = 'fit';
				
				activeFitObject = fitDeallocations.find(
					(fit) => fit.deallocationId === dealloc.id
				);
				activeFitId = activeFitObject?.fitId;
				if (dealloc?.suggestion) labelType = 'fit-opt-match';
			} else {
				if (dealloc?.suggestion) labelType = 'opt-match';
			}

			return (
				<React.Fragment key={dealloc.id}>
					{isPeopleSuggestion ? (
						<FitPeopleLabel type={labelType}>
							<div className={`fit-people ${labelType}`}>
							{activeFitObject?.responsibleId === user.id && (
								<Tooltip>
									<div className='opportunity-fit__undo'>
										<Button
											block
											icon={<UndoOutlined />}
											onClick={() => setUndoFitId(activeFitId)}
											className='opportunity-fit__undo_button'
											aria-label={t('undoFit')}
										>
											<span className='opportunity-fit__undo_text'>
												{t('undoFit')}
											</span>
										</Button>
									</div>
								</Tooltip>)}
								<PeopleItem people={dealloc} className={'remove-border'} />
							</div>
						</FitPeopleLabel>
					) : labelType === 'opt-match' ? (
						renderCheckbox(labelType, dealloc)
					) : (
						renderCheckbox(null, dealloc)
					)}
				</React.Fragment>
			);
		});

	/**
	 * Call onSave method upon saving Fit.
	 */
	// const onOk = useCallback(() => {
	// 	onSave(deallocationIds, needId);
	// }, [onSave, deallocationIds, needId]);

	const onOk = useCallback(() => {
		setIsConfirming(true)
		setFitData({deallocation: deallocations.find(deallocation => deallocation.id === deallocationIds[0]), need})
}, [deallocationIds, deallocations, need]);

const onConfirmFit = useCallback(() => {
		onSave(deallocationIds, needId);
}, [deallocationIds, needId, onSave])

	/**
	 * Call onClose method upon closing or canceling modal.
	 */
	const onCancel = useCallback(() => {
		if (loading) {
			return;
		}

		onClose();
	}, [loading, onClose]);

		/**
	 * Returns a string indicating whether the word "fit" or "fits" should be used based on the number of deallocations.
	 *
	 * @param {number} length - The number of deallocations.
	 * @returns {string} A string indicating whether the word "fit" or "fits" should be used.
	 */
	const opportunityIsSingular = need.deallocations.length >= 2 ? "fits" : "fit"

		/**
	 * Returns a string indicating whether the opportunity has active deallocations or not.
	 *
	 * @param {Object} need - The need object to check.
	 * @param {Array} need.deallocations - An array of deallocations associated with the need.
	 * @param {Function} t - The translation function to use.
	 * @returns {string} A string indicating whether the opportunity has active deallocations or not.
	 */ 
  const opportunityAlert = t("opportunityHasFit", {opportunityValue: need.deallocations.length, opportunitySingular: opportunityIsSingular})

	if (undoFitId) {
		return (
			<UndoFitReasonModal
				onCancel={() => setUndoFitId(null)}
				onConfirm={async (cancelCategory, cancelSubcategory) => {
					setUndoFitLoading(true);
					try {
						await onCancelFit(undoFitId, cancelCategory, cancelSubcategory);
					} finally {
						setUndoFitLoading(false);
						setUndoFitId(null);
					}
				}}
				loading={undoFitLoading}
			/>
		);
	}

	return isConfirming ? (
    <ConfirmFitModal
      onConfirm={onConfirmFit}
      fitData={fitData}
	  loading={loading}
      deallocations={deallocations}
			deallocationIds={deallocationIds}
			type="needs"
      onCancel={() => setIsConfirming(false)}
			/>
			) : (
				<Modal
				title={t('suggestFit')}
				width={1000}
				className="opportunity-fit"
				visible={true}
				confirmLoading={loading}
				onCancel={onCancel}
				onOk={onOk}
				okButtonProps={{
					className: 'cit-btn primary-type',
					// if there was no change, disable 'ok' button
					disabled:
					areEqualArrays(deallocationFitIds, deallocationIds) ||
					deallocationIds.length === 0
					? true
					: false,
				}}
				cancelButtonProps={{
					className: 'cit-btn secondary-type link-type cancel-button',
				}}
				cancelText={t('back')}
				okText={t('confirmFit')}
				>
      <div className="opportunity-fit__subtitle">
        <Row className="opportunity-fit__filters" align="top">
          <Col span={14}>
            <div className="opportunity-fit__filters_gutter">
              {t('listRightAllNeeds')}
            </div>
            <FormItem label={t('filter')} id='fit-modal'>
              <Search
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
								aria-labelledby='fit-modal'
								aria-label={t('fitOpportunityFilter')}
								role='searchbox'
              />
            </FormItem>
          </Col>
        </Row>
        <Row className="opportunity-radio" align="bottom">
          <Col span={24}>
            <Radio.Group
              name="radiogroup"
              value={isFiltered}
              onChange={(e) => setIsFiltered(e.target.value)}
            >
              <div className="opportunity-radio__filter_align">
                <Radio value={true}>
                  <span className="opportunity-radio__filter_text">
                    {t('filteredOpportunitiesOnly')}
                  </span>
                </Radio>
                <Radio value={false}>
                  <span className="opportunity-radio__filter_text">
                    {t('allOpportunities')}
                  </span>
                </Radio>
              </div>
            </Radio.Group>
          </Col>
        </Row>
      </div>

      <div className="opportunity-fit__container">
        <FormItem label={t('needsTitle')}>
          <div className="opportunity-fit__container_options">
            <NeedsItem need={need} className={'is-need-page'} />
          </div>
        </FormItem>
        <div className="opportunity-fit__container_arrow">
          <img
            src={DoubleArrowImage}
            alt="match arrow"
            className="match-arrow-image"
          />
        </div>
        <div className="opportunity-fit__container_options">
          {deallocationsOptions.length > 0 ? (
            <Checkbox.Group>{deallocationsOptions}</Checkbox.Group>
          ) : (
            <p style={{ textAlign: 'center' }}>
              {t('noMatchingDeallocations')}
            </p>
          )}
        </div>
      </div>
			<div className='opportunity-fit__alert' tabIndex={0} role="alert" aria-describedby='alert-container'>
        <img src={Exclamation} alt="exclamation" role='none'/>
        <span role='alert' >{opportunityAlert}</span>
      </div>
    </Modal>
  );
}

FitModal.defaultProps = {
	loading: false,
	need: {},
	deallocations: [],
	onSave: noop,
	onClose: noop,
	positionMaps: {},
	onCancelFit: noop,
};

export default FitModal;
